import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import EmailEditor from '../components/EmailEditor/EmailEditor';
import { useCRM } from '../context/CRMContext';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot,
  serverTimestamp, orderBy, query, where,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const TYPE_ICONS  = { call: '📞', email: '📧', whatsapp: '💬', meeting: '🗓' };
const TYPE_COLORS = { call: '#10b981', email: '#3b82f6', whatsapp: '#25d366', meeting: '#7c3aed' };

const seqCol = () => collection(db, 'sequences');

const leadDisplayName = (lead) => {
  if (!lead) return '';
  const first = lead.firstName || '';
  const last  = lead.lastName  || '';
  if (first || last) return (first + ' ' + last).trim();
  return lead.name || lead.email || lead.phone || '';
};

const STEP_TYPES = [
  { id: 'email',    label: 'Send Email',        icon: '📧' },
  { id: 'whatsapp', label: 'Send WhatsApp',     icon: '💬' },
  { id: 'call',     label: 'Schedule Call',     icon: '📞' },
  { id: 'sms',      label: 'Send SMS',          icon: '📱' },
  { id: 'wait',     label: 'Wait (delay only)', icon: '⏳' },
];
const SEQ_COLORS = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899'];
const SEQ_ICONS  = ['🎉','📋','🎓','🔥','💼','⭐','🚀','📞','📧','🌟'];

export default function Followups() {
  const { followups, leads, users, companyId, addFollowup, updateFollowup, deleteFollowup, getLeadById, getUserById, setCurrentPage, setOpenLeadId } = useCRM();

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType,   setFilterType]   = useState('all');
  const [filterUser,   setFilterUser]   = useState('all');
  const [tab,          setTab]          = useState('tasks');
  const [showAdd,      setShowAdd]      = useState(false);
  const [form, setForm] = useState({
    leadId: '', type: 'call', title: '', dueDate: '', notes: '', priority: 'medium', assignedTo: '',
  });

  // Tick every 60s to keep date labels fresh
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // In-app toast
  const [toast, setToast]   = useState(null);
  const toastTimer          = useRef(null);
  const showToast = useCallback((msg, type = 'warning') => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 7000);
  }, []);

  // Browser / in-app notifications
  const notifiedRef = useRef(new Set());
  useEffect(() => {
    const now      = new Date();
    const todayStr = now.toDateString();
    const overdue  = followups.filter(f => f.status !== 'completed' && (f.status === 'overdue' || new Date(f.dueDate) < now));
    const dueToday = followups.filter(f => {
      if (f.status === 'completed' || new Date(f.dueDate) < now) return false;
      return new Date(f.dueDate).toDateString() === todayStr;
    });
    const newOverdue  = overdue.filter(f  => !notifiedRef.current.has(f.id + '_ov'));
    const newDueToday = dueToday.filter(f => !notifiedRef.current.has(f.id + '_td'));

    if (newOverdue.length > 0) {
      newOverdue.forEach(f => notifiedRef.current.add(f.id + '_ov'));
      showToast(`⚠️ ${newOverdue.length} follow-up${newOverdue.length > 1 ? 's are' : ' is'} overdue`, 'danger');
      const fireNotif = () => new Notification('Overdue Follow-ups', {
        body: `You have ${newOverdue.length} overdue follow-up${newOverdue.length > 1 ? 's' : ''}`,
        icon: '/favicon.ico',
      });
      if (Notification.permission === 'granted') fireNotif();
      else if (Notification.permission === 'default') {
        Notification.requestPermission().then(p => { if (p === 'granted') fireNotif(); });
      }
    } else if (newDueToday.length > 0) {
      newDueToday.forEach(f => notifiedRef.current.add(f.id + '_td'));
      showToast(`📅 ${newDueToday.length} follow-up${newDueToday.length > 1 ? 's' : ''} due today`, 'info');
    }
  }, [followups, tick, showToast]);

  // Sequences
  const [sequences,  setSequences]  = useState([]);
  const [seqLoading, setSeqLoading] = useState(true);
  const [showNewSeq, setShowNewSeq] = useState(false);
  const [editSeq,    setEditSeq]    = useState(null);
  const [seqForm, setSeqForm] = useState({
    name: '', description: '', icon: '🎉', color: '#7c3aed',
    steps: [{ delay: 0, type: 'email', title: '', message: '' }],
  });

  useEffect(() => {
    // Sequences were previously fetched with NO companyId filter at all —
    // every company saw and edited the exact same global list. Block the
    // query entirely until we have a real companyId; never fetch unscoped.
    if (!companyId) { setSequences([]); setSeqLoading(false); return; }
    const q = query(seqCol(), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q,
      snap => { setSequences(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setSeqLoading(false); },
      ()   => setSeqLoading(false)
    );
    return () => unsub();
  }, [companyId]);

  const setSeq = (k, v) => setSeqForm(p => ({ ...p, [k]: v }));

  const addStep = () => setSeqForm(p => ({
    ...p, steps: [...p.steps, { delay: 1, type: 'email', title: '', message: '' }],
  }));

  const removeStep = (idx) => setSeqForm(p => ({
    ...p, steps: p.steps.filter((_, i) => i !== idx),
  }));

  const updateStep = (idx, k, v) => setSeqForm(p => {
    const steps = [...p.steps];
    steps[idx] = { ...steps[idx], [k]: v };
    return { ...p, steps };
  });

  const openNewSeq = () => {
    setEditSeq(null);
    setSeqForm({ name: '', description: '', icon: '🎉', color: '#7c3aed',
      steps: [{ delay: 0, type: 'email', title: '', message: '' }] });
    setShowNewSeq(true);
  };

  const openEditSeq = (seq) => {
    setEditSeq(seq);
    setSeqForm({
      name: seq.name, description: seq.description || '',
      icon: seq.icon || '🎉', color: seq.color || '#7c3aed',
      steps: seq.steps?.length ? seq.steps : [{ delay: 0, type: 'email', title: '', message: '' }],
    });
    setShowNewSeq(true);
  };

  const saveSeq = async () => {
    if (!seqForm.name.trim() || !companyId) return;
    if (editSeq) {
      await updateDoc(doc(db, 'sequences', editSeq.id), { ...seqForm, updatedAt: serverTimestamp() });
    } else {
      // companyId stamped on create — required for the per-company filter above to work.
      await addDoc(seqCol(), { ...seqForm, companyId, active: false, createdAt: serverTimestamp() });
    }
    setShowNewSeq(false);
  };

  const deleteSeq = async (id) => {
    if (window.confirm('Delete this sequence? This cannot be undone.')) {
      await deleteDoc(doc(db, 'sequences', id));
    }
  };

  const toggleSeq = (seq) =>
    updateDoc(doc(db, 'sequences', seq.id), { active: !seq.active });

  // Follow-up form
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAdd = () => {
    if (!form.title || !form.dueDate) return;
    addFollowup({ ...form, status: 'pending' });
    setForm({ leadId: '', type: 'call', title: '', dueDate: '', notes: '', priority: 'medium', assignedTo: '' });
    setShowAdd(false);
  };

  // Filtered tasks — tick forces re-sort each minute
  const now      = new Date();
  const todayStr = now.toDateString();

  const filtered = useMemo(() => {
    let arr = [...followups];
    if (filterStatus !== 'all') arr = arr.filter(f => f.status === filterStatus);
    if (filterType   !== 'all') arr = arr.filter(f => f.type === filterType);
    if (filterUser   !== 'all') arr = arr.filter(f => f.assignedTo === filterUser);
    arr.sort((a, b) => {
      const aOv = a.status === 'overdue' || (a.status !== 'completed' && new Date(a.dueDate) < now);
      const bOv = b.status === 'overdue' || (b.status !== 'completed' && new Date(b.dueDate) < now);
      if (aOv && !bOv) return -1;
      if (!aOv && bOv) return 1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followups, filterStatus, filterType, filterUser, tick]);

  const overdueTasks   = filtered.filter(f => f.status !== 'completed' && (f.status === 'overdue' || new Date(f.dueDate) < now));
  const todayTasks     = filtered.filter(f => f.status !== 'completed' && f.status !== 'overdue' && new Date(f.dueDate) >= now && new Date(f.dueDate).toDateString() === todayStr);
  const upcomingTasks  = filtered.filter(f => f.status === 'pending' && new Date(f.dueDate) > now && new Date(f.dueDate).toDateString() !== todayStr);
  const completedTasks = filtered.filter(f => f.status === 'completed');

  const formatDate = (d) => {
    if (!d) return 'No date';
    const date     = new Date(d);
    const diffMs   = date - new Date();
    const diffDays = Math.round(diffMs / 86400000);
    if (diffMs < 0 && Math.abs(diffDays) === 0) return 'Due today';
    if (diffMs < 0) return Math.abs(diffDays) + 'd overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const getDateColor = (f) => {
    if (f.status === 'completed') return '#34d399';
    const diff = new Date(f.dueDate) - new Date();
    if (diff < 0) return '#f87171';
    if (diff < 86400000) return '#fbbf24';
    return '#64748b';
  };

  // Sub-components
  const StatPill = ({ label, count, color }) => (
    <div style={{ background: color + '15', border: '1px solid ' + color + '30', borderRadius: 10, padding: '10px 16px', textAlign: 'center', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{count}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
    </div>
  );

  const Toggle = ({ value, onChange }) => (
    <div onClick={onChange} title={value ? 'Active — click to deactivate' : 'Inactive — click to activate'}
      style={{ width: 40, height: 22, borderRadius: 11, background: value ? '#10b981' : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
    </div>
  );

  const TaskCard = ({ f }) => {
    const lead      = getLeadById(f.leadId);
    const user      = getUserById(f.assignedTo);
    const dateColor = getDateColor(f);
    const isOverdue = f.status !== 'completed' && (f.status === 'overdue' || new Date(f.dueDate) < new Date());
    const lName     = lead ? leadDisplayName(lead) : null;
    return (
      <div style={{ padding: '14px 16px', background: isOverdue ? 'rgba(239,68,68,0.04)' : 'rgba(0,0,0,0.03)', border: '1px solid ' + (isOverdue ? 'rgba(239,68,68,0.2)' : 'rgba(0,0,0,0.05)'), borderRadius: 12, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: (TYPE_COLORS[f.type] || '#7c3aed') + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
          {TYPE_ICONS[f.type] || '📌'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: f.status === 'completed' ? '#475569' : '#0f172a', textDecoration: f.status === 'completed' ? 'line-through' : 'none' }}>{f.title}</span>
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 600, background: f.priority === 'high' ? 'rgba(239,68,68,0.15)' : f.priority === 'medium' ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)', color: f.priority === 'high' ? '#f87171' : f.priority === 'medium' ? '#fbbf24' : '#34d399' }}>
              {f.priority}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {lName && (
                <span
                  onClick={() => { setOpenLeadId(f.leadId); setCurrentPage('leads'); }}
                  style={{ fontSize: 11, color: '#7c3aed', fontWeight: 500, background: 'rgba(124,58,237,0.1)', padding: '2px 7px', borderRadius: 4, cursor: 'pointer', textDecoration: 'underline dotted' }}
                  title="Go to lead"
                >{lName} ↗</span>
              )}
            <span style={{ fontSize: 11, color: dateColor, fontWeight: 600 }}>🕐 {formatDate(f.dueDate)}</span>
            {user && <span style={{ fontSize: 11, color: '#64748b' }}>→ {(user.name || 'Unknown').split(' ')[0]}</span>}
          </div>
          {f.notes && <div style={{ fontSize: 11, color: '#475569', marginTop: 4, fontStyle: 'italic' }}>"{f.notes}"</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {f.status !== 'completed' && (
            <button onClick={() => updateFollowup(f.id, { status: 'completed' })}
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.25)', color: '#34d399', borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>✓ Done</button>
          )}
          {f.status === 'completed' && (
            <button onClick={() => updateFollowup(f.id, { status: 'pending' })}
              style={{ background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)', color: '#64748b', borderRadius: 7, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>↩ Undo</button>
          )}
          <button onClick={() => deleteFollowup(f.id)}
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 7, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>✕</button>
        </div>
      </div>
    );
  };

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '12px 18px', borderRadius: 10, maxWidth: 360, background: toast.type === 'danger' ? '#fef2f2' : '#eff6ff', border: '1px solid ' + (toast.type === 'danger' ? 'rgba(239,68,68,0.35)' : 'rgba(59,130,246,0.35)'), color: toast.type === 'danger' ? '#dc2626' : '#2563eb', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 10, animation: 'fuSlideIn 0.3s ease' }}>
          <span style={{ flex: 1 }}>{toast.msg}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
      )}

      {/* Stats bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <StatPill label="Overdue"   count={overdueTasks.length}   color="#ef4444" />
          <StatPill label="Today"     count={todayTasks.length}     color="#f59e0b" />
          <StatPill label="Upcoming"  count={upcomingTasks.length}  color="#3b82f6" />
          <StatPill label="Completed" count={completedTasks.length} color="#10b981" />
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        <div className="tab-bar">
          {['tasks', 'sequences'].map(t => (
            <div key={t} className={'tab-item' + (tab === t ? ' active' : '')} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</div>
          ))}
        </div>

        {tab === 'tasks' && (
          <>
            <select className="crm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ height: 34, fontSize: 13, minWidth: 130 }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
            </select>
            <select className="crm-select" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ height: 34, fontSize: 13, minWidth: 130 }}>
              <option value="all">All Types</option>
              <option value="call">📞 Call</option>
              <option value="email">📧 Email</option>
              <option value="whatsapp">💬 WhatsApp</option>
              <option value="meeting">🗓 Meeting</option>
            </select>
            <select className="crm-select" value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ height: 34, fontSize: 13, minWidth: 150 }}>
              <option value="all">All Counsellors</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </>
        )}
        <button className="btn-primary" onClick={() => tab === 'tasks' ? setShowAdd(true) : openNewSeq()} style={{ marginLeft: 'auto', padding: '7px 16px', fontSize: 13 }}>
          {tab === 'tasks' ? '+ Schedule Follow-up' : '+ New Sequence'}
        </button>
      </div>

      {/* ── Tasks tab ── */}
      {tab === 'tasks' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>All clear!</div>
              <div style={{ fontSize: 13 }}>No follow-ups match your filters.</div>
            </div>
          )}

          {overdueTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f87171' }}>⚠️ Overdue</div>
                <span style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{overdueTasks.length}</span>
              </div>
              {overdueTasks.map(f => <TaskCard key={f.id} f={f} />)}
            </div>
          )}

          {todayTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24' }}>📅 Due Today</div>
                <span style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{todayTasks.length}</span>
              </div>
              {todayTasks.map(f => <TaskCard key={f.id} f={f} />)}
            </div>
          )}

          {upcomingTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>🗓 Upcoming</div>
                <span style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{upcomingTasks.length}</span>
              </div>
              {upcomingTasks.map(f => <TaskCard key={f.id} f={f} />)}
            </div>
          )}

          {completedTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#34d399' }}>✅ Completed</div>
                <span style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{completedTasks.length}</span>
              </div>
              {completedTasks.map(f => <TaskCard key={f.id} f={f} />)}
            </div>
          )}
        </div>
      )}

      {/* ── Sequences tab ── */}
      {tab === 'sequences' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* Explanation banner */}
          <div style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.07) 0%,rgba(59,130,246,0.07) 100%)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 14, padding: '16px 20px', marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}>📋 What are Sequences?</div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.65 }}>
              A <strong>Sequence</strong> is a pre-built series of follow-up steps you can apply to any lead. Each step has a <strong>delay</strong> (how many days after the previous step), an <strong>action type</strong> (email, WhatsApp, call, etc.), and a <strong>message template</strong>. When you activate a sequence, it acts as your playbook — reminding your counsellors exactly what to do and when.
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              {[['1️⃣','Create a sequence','Define the steps: Day 0 intro email, Day 3 WhatsApp follow-up, Day 7 call'],['2️⃣','Activate it','Toggle it ON to make it available to counsellors'],['3️⃣','Apply to leads','Counsellors assign a sequence to a lead from the lead detail page']].map(([num, title, desc]) => (
                <div key={num} style={{ flex: 1, minWidth: 180, background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 16, marginBottom: 4 }}>{num}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{title}</div>
                  <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {seqLoading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>Loading sequences…</div>}

          {!seqLoading && sequences.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No sequences yet</div>
              <div style={{ fontSize: 13, marginBottom: 20 }}>Create your first follow-up sequence to get started.</div>
              <button className="btn-primary" onClick={openNewSeq}>+ Create First Sequence</button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {sequences.map(seq => (
              <div key={seq.id} className="glass-card" style={{ padding: 20, border: '1.5px solid ' + (seq.active ? 'rgba(16,185,129,0.25)' : 'rgba(0,0,0,0.06)'), transition: 'border 0.2s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: (seq.color || '#7c3aed') + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {seq.icon || '📋'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{seq.name}</div>
                    {seq.description && <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{seq.description}</div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <Toggle value={!!seq.active} onChange={() => toggleSeq(seq)} />
                    <span style={{ fontSize: 10, color: seq.active ? '#10b981' : '#94a3b8', fontWeight: 600 }}>{seq.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>

                {/* Steps preview */}
                <div style={{ marginBottom: 14 }}>
                  {(seq.steps || []).map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: (seq.color || '#7c3aed') + '20', color: seq.color || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1, fontSize: 12, color: '#374151' }}>
                        {step.delay === 0 ? 'Day 0' : '+' + step.delay + 'd'} — {(STEP_TYPES.find(s => s.id === step.type) || STEP_TYPES[0]).icon} {step.title || step.type}
                      </div>
                    </div>
                  ))}
                  {(seq.steps || []).length === 0 && <div style={{ fontSize: 12, color: '#94a3b8' }}>No steps defined</div>}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEditSeq(seq)} style={{ flex: 1, padding: '7px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: '#475569', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>✏️ Edit</button>
                  <button onClick={() => deleteSeq(seq.id)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.06)', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Add Follow-up Modal ── */}
      {showAdd && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="modal-box" style={{ maxWidth: 540 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Schedule Follow-up</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Title *</label>
                <input className="crm-input" placeholder="e.g. Call back re: UK visa" value={form.title} onChange={e => set('title', e.target.value)} style={{ width: '100%' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Type</label>
                  <select className="crm-select" style={{ width: '100%' }} value={form.type} onChange={e => set('type', e.target.value)}>
                    <option value="call">📞 Call</option>
                    <option value="email">📧 Email</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="meeting">🗓 Meeting</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Due Date *</label>
                  <input className="crm-input" type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Priority</label>
                  <select className="crm-select" style={{ width: '100%' }} value={form.priority} onChange={e => set('priority', e.target.value)}>
                    <option value="high">🔴 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Assigned To</label>
                  <select className="crm-select" style={{ width: '100%' }} value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Linked Lead</label>
                <select className="crm-select" style={{ width: '100%' }} value={form.leadId} onChange={e => set('leadId', e.target.value)}>
                  <option value="">— Not linked to a lead —</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{leadDisplayName(l) || l.email}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Notes</label>
                <textarea className="crm-textarea" placeholder="Optional notes…" value={form.notes} onChange={e => set('notes', e.target.value)} style={{ minHeight: 64, width: '100%' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleAdd} disabled={!form.title || !form.dueDate}>Schedule</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New / Edit Sequence Modal ── */}
      {showNewSeq && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNewSeq(false)}>
          <div className="modal-box" style={{ maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{editSeq ? 'Edit Sequence' : 'New Sequence'}</h2>
              <button onClick={() => setShowNewSeq(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {/* Name + description */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Sequence Name *</label>
                  <input className="crm-input" placeholder="e.g. UK Visa Inquiry Follow-up" value={seqForm.name} onChange={e => setSeq('name', e.target.value)} style={{ width: '100%' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Description (optional)</label>
                  <input className="crm-input" placeholder="Brief description of when to use this sequence" value={seqForm.description} onChange={e => setSeq('description', e.target.value)} style={{ width: '100%' }} />
                </div>
              </div>

              {/* Icon + colour */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 8 }}>Icon & Colour</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {SEQ_ICONS.map(ic => (
                      <div key={ic} onClick={() => setSeq('icon', ic)}
                        style={{ width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', border: seqForm.icon === ic ? '2px solid #7c3aed' : '2px solid transparent', background: seqForm.icon === ic ? 'rgba(124,58,237,0.1)' : 'rgba(0,0,0,0.04)' }}>
                        {ic}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {SEQ_COLORS.map(c => (
                      <div key={c} onClick={() => setSeq('color', c)}
                        style={{ width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer', border: seqForm.color === c ? '3px solid #0f172a' : '3px solid transparent' }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Steps</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Define the actions in order. Each step runs after the specified delay from the previous step.</div>
                  </div>
                  <button onClick={addStep} style={{ padding: '6px 14px', borderRadius: 8, border: '1.5px dashed #7c3aed', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>+ Add Step</button>
                </div>

                {seqForm.steps.map((step, i) => (
                  <div key={i} style={{ padding: '14px', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: (seqForm.color || '#7c3aed') + '25', color: seqForm.color || '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{i + 1}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', flex: 1 }}>Step {i + 1}</div>
                      {seqForm.steps.length > 1 && (
                        <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 14 }}>✕</button>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Delay (days)</label>
                        <input className="crm-input" type="number" min="0" value={step.delay} onChange={e => updateStep(i, 'delay', Number(e.target.value))} style={{ width: '100%', height: 34 }} />
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{i === 0 ? 'Day 0 = immediately' : 'Days after step ' + i}</div>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Action Type</label>
                        <select className="crm-select" style={{ width: '100%', height: 34 }} value={step.type} onChange={e => updateStep(i, 'type', e.target.value)}>
                          {STEP_TYPES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                        </select>
                      </div>
                    </div>
                    {step.type !== 'wait' && (
                      <>
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Step Title / Subject</label>
                          <input className="crm-input" placeholder="e.g. Intro email — scholarship options" value={step.title} onChange={e => updateStep(i, 'title', e.target.value)} style={{ width: '100%' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                            {step.type === 'email' ? 'Email Body' : 'Message Template (optional)'}
                          </label>
                          {step.type === 'email' ? (
                            <EmailEditor value={step.message || ''} onChange={v => updateStep(i, 'message', v)} compact />
                          ) : (
                            <textarea className="crm-input" placeholder="Hi {{name}}, just following up on your enquiry…" value={step.message || ''} onChange={e => updateStep(i, 'message', e.target.value)} rows={2} style={{ resize: 'vertical', width: '100%' }} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ flexShrink: 0, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setShowNewSeq(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveSeq} disabled={!seqForm.name.trim()}>
                {editSeq ? 'Save Changes' : 'Create Sequence'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fuSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
